// /join/<CODE>[?src=qr] (FR-005 to FR-020; P-JOIN-2 to P-JOIN-7, P-JOIN-10; research R5). Opens
// signed in or not. The address is normalised to the upper-case code; malformed codes never reach
// the lookup. decideJoinView picks the view; this screen renders it and runs the side effects:
// pending join on Continue / Log in / Not now, clearIfCode on refusals, the hand-off for parents,
// the switch to parent mode for web dual-role users, and the parent-role offer for school admins.
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useRef, useState } from "react";
import { StyleSheet, View } from "react-native";

import { useSession, useSessionController } from "@/session/hooks";
import { usePlatform } from "@/session/platform";
import { StateView } from "@/shell/StateView";
import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";
import { InlineMessage } from "@/ui/InlineMessage";
import { LinkButton } from "@/ui/LinkButton";
import { Text } from "@/ui/Text";

import { JOIN_COPY } from "./copy";
import { decideJoinView, landingSource } from "./decideJoinView";
import { addStudentHref, handoff } from "./handoff";
import { JoinLayout } from "./JoinLayout";
import { sourceFromLink } from "./joinSource";
import { ParentRoleOffer } from "./ParentRoleOffer";
import { type PendingJoin, pendingJoin } from "./pendingJoin";
import { isValidSchoolCode, normaliseSchoolCode } from "./schoolCode";
import { SchoolSummaryCard } from "./SchoolSummaryCard";
import { useSchoolLookup } from "./useSchoolLookup";
import { WayOn } from "./WayOn";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    stack: { gap: t.space.md },
    row: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "center",
      gap: t.space.xs,
    },
  }),
);

function firstParam(v: string | string[] | undefined): string {
  return typeof v === "string" ? v : Array.isArray(v) ? (v[0] ?? "") : "";
}

export function JoinLandingScreen({ rawCode }: { rawCode?: string } = {}) {
  const styles = useStyles();
  const params = useLocalSearchParams<{ code?: string; src?: string }>();
  const router = useRouter();
  const session = useSession();
  const platform = usePlatform();
  const controller = useSessionController();

  const raw = rawCode ?? firstParam(params.code);
  const code = normaliseSchoolCode(raw);
  const codeValid = isValidSchoolCode(code);

  // `/join/gps7k2` → `/join/GPS7K2` (the address shows the normalised code).
  useEffect(() => {
    if (rawCode === undefined && codeValid && raw !== code)
      router.setParams({ code });
  }, [rawCode, codeValid, raw, code, router]);

  const { state: lookup, retry } = useSchoolLookup(codeValid ? code : null);

  const [pending, setPending] = useState<PendingJoin | null | undefined>(
    undefined,
  );
  useEffect(() => {
    let live = true;
    void pendingJoin.read().then((p) => live && setPending(p));
    return () => {
      live = false;
    };
  }, []);

  const source = landingSource(
    params.src,
    pending ?? null,
    code,
    sourceFromLink,
  );
  const decided = decideJoinView({
    codeValid,
    lookup,
    session,
    platform,
    source,
  });
  const view =
    pending === undefined && decided.kind !== "invalid"
      ? ({ kind: "loading" } as const)
      : decided;

  // Refusals remove a pending join for this same code only (FR-008, FR-020).
  useEffect(() => {
    if (view.kind === "invalid" || view.kind === "notActive")
      void pendingJoin.clearIfCode(code);
  }, [view.kind, code]);

  // Signed-in parent: straight to Add student (FR-017, FR-021; P-JOIN-6). The newest scan or link
  // also becomes the pending join, replacing an older one, so Add child opens on this school later
  // too (amendment 2026-10-08: the last scanned school wins).
  const handedOff = useRef(false);
  useEffect(() => {
    if (view.kind !== "handOff" || handedOff.current) return;
    handedOff.current = true;
    const { source, school } = view;
    void pendingJoin.save(code, Date.now(), source).finally(() => {
      handoff.put({ code, source, school });
      router.replace(addStudentHref(code, source));
    });
  }, [view, code, router]);

  // Web dual role in school-admin mode: parent mode first (FR-018).
  useEffect(() => {
    if (view.kind === "switchThenHandOff") void controller.switchRole("PARENT");
  }, [view.kind, controller]);

  async function keepAndGo(href: "/welcome" | "/login") {
    if (view.kind !== "welcome") return;
    await pendingJoin.save(code, Date.now(), view.source, view.school.name);
    router.push(href);
  }

  async function notNow() {
    await pendingJoin.clear();
    router.replace("/welcome");
  }

  let body;
  switch (view.kind) {
    case "loading":
    case "handOff":
    case "switchThenHandOff":
      body = <StateView status="loading" />;
      break;
    case "invalid":
      body = (
        <View style={styles.stack} testID="join-invalid">
          <InlineMessage tone="error">{JOIN_COPY.invalid}</InlineMessage>
          <WayOn signedIn={view.signedIn} />
        </View>
      );
      break;
    case "notActive":
      body = (
        <View style={styles.stack} testID="join-not-active">
          <InlineMessage tone="warning">
            {JOIN_COPY.notActive(view.name)}
          </InlineMessage>
          <WayOn signedIn={view.signedIn} />
        </View>
      );
      break;
    case "rateLimited":
      body = (
        <View style={styles.stack} testID="join-rate-limited">
          <InlineMessage tone="warning">{JOIN_COPY.rateLimited}</InlineMessage>
          <WayOn signedIn={view.signedIn} />
        </View>
      );
      break;
    case "error":
      body = <StateView status="error" error={view.error} onRetry={retry} />;
      break;
    case "parentRoleOffer":
      body = (
        <ParentRoleOffer
          platform={view.platform}
          onParent={() => {
            handedOff.current = false;
            retry();
          }}
        />
      );
      break;
    case "welcome":
      body = (
        <View style={styles.stack} testID="join-welcome">
          <SchoolSummaryCard school={view.school}>
            <Text>
              {JOIN_COPY.landing.joiningPrefix}
              <Text weight="bold">{view.school.name}</Text>
              {JOIN_COPY.landing.joiningSuffix}
            </Text>
            <Button onPress={() => void keepAndGo("/welcome")}>
              {JOIN_COPY.landing.continue}
            </Button>
            <Button variant="outline" onPress={() => void notNow()}>
              {JOIN_COPY.landing.notNow}
            </Button>
          </SchoolSummaryCard>
          <View style={styles.row}>
            <Text tone="muted">{JOIN_COPY.landing.haveAccount}</Text>
            <LinkButton role="link" onPress={() => void keepAndGo("/login")}>
              {JOIN_COPY.landing.logIn}
            </LinkButton>
          </View>
        </View>
      );
      break;
  }

  return <JoinLayout testID="join-landing">{body}</JoinLayout>;
}
